/**
 * Posting into a channel.
 *
 * A thin binding over the shared `ChatComposer`: the editor, the invisible uploader,
 * the audio recorder, Mod-Enter, drag-and-drop, the disabled-while-uploading rule and
 * the draft restore-on-failure all already live there. What this component adds is the
 * CHANNEL half — the create mutation, the draft key, the typing announcement's cid,
 * and the capability gates that come off the channel's own feature flags.
 *
 * `onSend` must reject on failure: the composer empties the box at press time
 * (`sendMode: "instant"` — a conversation is a stream, and making the writer
 * wait on the round trip is what used to leave the form state fighting the editor),
 * and a rejection is the only signal that can put the message back. A silent resolve
 * loses it.
 */

import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button/button";
import { BwArrowUpMdFill, BwPlusLine } from "@/components/ui/icons";
import { SendButton } from "@/components/ui/message";
import {
  ChatComposer,
  type ChatComposerApi,
  useChatComposerForm,
} from "@/features/shared/chat-composer";
import type { ChannelQuotedMessage } from "../_api/channel-endpoints";
import { useChannelMessageMutations } from "../_api/use-channel-message-mutations";
import { useChannelDraft } from "../_hooks/use-channel-draft";
import { buildDefaultChannelMessageInput } from "../_libs/composer-input";
import { renderComposerSlot } from "../_libs/composer-slot";
import type {
  ChannelComposerConfig,
  ChannelSendContext,
  ResolvedChannelSurface,
} from "../_types/channel-surface";
import { ChannelQuoteCard } from "./channel-quote-card";

export type ChannelComposerProps = {
  cid: string;
  surface: ResolvedChannelSurface;
  /**
   * Where the unsent text is kept. Defaults to the channel — a thread passes its own so
   * a half-written reply does not reappear in the main conversation's box.
   */
  draftKey?: string;
  /** `Message.id` this post replies to. Set by the thread view. */
  replyToId?: string;
  /**
   * The message the next post quotes ("Quote reply"), shown as a "Reply to <author>" card in
   * the box. Held by the view, which is where the list's Reply action writes it.
   */
  quotedMessage?: ChannelQuotedMessage | null;
  /**
   * Called with `null` when the quote is spent or dismissed, and with the quote again
   * when its send fails — the box restores the text, so it restores the quote with it.
   */
  onQuotedMessageChange?: (quote: ChannelQuotedMessage | null) => void;
  onSent?: () => void;
};

export function ChannelComposer({
  cid,
  surface,
  draftKey,
  replyToId,
  quotedMessage,
  onQuotedMessageChange,
  onSent,
}: ChannelComposerProps) {
  const config: ChannelComposerConfig =
    surface.composer === false ? {} : surface.composer;
  // The quote of the send that just failed, held until the box decides whether it puts
  // that message back (`onFailedSendRestored`) — only then does the quote come back too.
  const failedQuoteRef = useRef<ChannelQuotedMessage | null>(null);
  // What the surface sent with that failed send, held the same way.
  const failedSendContextRef = useRef<ChannelSendContext | null>(null);
  const draft = useChannelDraft(draftKey ?? cid);
  const { sendMessage } = useChannelMessageMutations(cid);

  const composer = useChatComposerForm({
    source: { kind: "draft", draft },
    // The box comes back the instant Send is pressed — a conversation is a
    // stream, and waiting on the round trip to type the next message is what
    // made fast successive sends fight the form state.
    sendMode: "instant",
    errorTitle: "Can't send message!",
    maxCharacters: surface.maxCharacters,
    typing: surface.typing ? { cid } : undefined,
    // At the press, like the text: the quote belongs to the message being sent.
    onSent: () => {
      if (!quotedMessage) {
        onSent?.();
        return;
      }
      onQuotedMessageChange?.(null);
      // Dropping the card shrinks the box, which moves where "newest" sits — the view's
      // scroll-to-newest has to run after that layout, or it stops a card's height short.
      requestAnimationFrame(() => onSent?.());
    },
    onFailedSendRestored: () => {
      const quote = failedQuoteRef.current;
      failedQuoteRef.current = null;
      if (quote) onQuotedMessageChange?.(quote);
      const sendContext = failedSendContextRef.current;
      failedSendContextRef.current = null;
      sendContext?.onRestored?.();
    },
    onBeforeSend: config.onBeforeSend,
    errorDescription: config.sendErrorDescription,
    onSend: async (values) => {
      // First, in the press's own task: the box has just emptied, and what the surface
      // knows about its text leaves with this send.
      const sendContext = config.onSendStart?.();
      const buildInput = config.buildInput ?? buildDefaultChannelMessageInput;
      const input = {
        ...buildInput(values),
        ...(replyToId == null ? {} : { replyToId }),
        ...(quotedMessage ? { quotedMessageId: quotedMessage.id } : {}),
      };
      try {
        await sendMessage(input, {
          quotedMessage,
          headers: sendContext?.headers,
        });
      } catch (error) {
        // Every failure, before the box decides whether it puts the text back.
        sendContext?.onFailed?.();
        failedQuoteRef.current = quotedMessage ?? null;
        failedSendContextRef.current = sendContext ?? null;
        throw error;
      }
    },
  });

  return (
    <ChatComposer
      composer={composer}
      className="text-sm"
      placeholder={config.placeholder ?? "Write a message..."}
      // Every channel composer sends with the design's round primary arrow. The
      // labelled default stays for the surfaces that are NOT a conversation (the
      // quote compose dialog), where a button reading "Send" is the whole point.
      sendSlot={({ requestSend, isSubmitting, isDisabled }) => (
        <SendButton
          label={config.sendLabel ?? "Send"}
          tooltipDescription={config.sendTooltip ?? "Send message"}
          icon={<BwArrowUpMdFill className="size-5" />}
          size="icon-md"
          onClick={requestSend}
          isLoading={isSubmitting}
          disabled={isDisabled}
        />
      )}
      extensions={surface.mentions}
      showMentionButton={!!surface.mentions}
      showAudioRecorder={!!config.audio && surface.attachments}
      enableFileDrop={(config.fileDrop ?? true) && surface.attachments}
      header={renderComposerSlot(config.header, composer)}
      innerHeader={
        quotedMessage ? (
          <ComposerQuote
            quote={quotedMessage}
            composer={composer}
            onDismiss={() => onQuotedMessageChange?.(null)}
          />
        ) : null
      }
      footer={renderComposerSlot(config.footer, composer)}
      leadingActions={
        renderComposerSlot(config.leadingActions, composer) ??
        (surface.attachments ? (
          <ChannelUploadTrigger
            uploadTriggerRef={composer.uploadTriggerRef}
            disabled={composer.isSubmitting}
          />
        ) : null)
      }
      trailingActions={renderComposerSlot(config.trailingActions, composer)}
    />
  );
}

/** The pending quote inside the box; choosing one puts the cursor in the editor. */
function ComposerQuote({
  quote,
  composer,
  onDismiss,
}: {
  quote: ChannelQuotedMessage;
  composer: ChatComposerApi;
  onDismiss: () => void;
}) {
  const { editorRef } = composer;
  useEffect(() => {
    if (quote.id) editorRef.current?.focus();
  }, [editorRef, quote.id]);

  return (
    <div className="p-2 pb-0">
      <ChannelQuoteCard
        quote={quote}
        titlePrefix="Reply to"
        onDismiss={onDismiss}
      />
    </div>
  );
}

/**
 * The default attachment button.
 *
 * `<Upload>` itself is rendered invisibly inside `ChatComposer`; this only clicks its
 * hidden trigger. Surfaces with a richer picker (the office gallery dialog) replace the
 * whole slot through `leadingActions`.
 */
function ChannelUploadTrigger({
  uploadTriggerRef,
  disabled,
}: {
  uploadTriggerRef: React.RefObject<any>;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      kind="soft"
      variant="neutral"
      size="icon-md"
      disabled={disabled}
      aria-label="Attach files"
      onClick={() => uploadTriggerRef.current?.click()}
    >
      <BwPlusLine className="size-5" />
    </Button>
  );
}
